import React from 'react';
import { Link } from 'react-router-dom';
import SpinningGraphic from '../components/SpinningGraphic';
import '../styles/Home.css';

const Home: React.FC = () => {
  const projects = [
    {
      title: 'Classic Games',
      description: 'Collection of 4 classic games built with React',
      path: '/classic-games',
      icon: '🎮',
      tech: ['React', 'TypeScript', 'Canvas API']
    },
    {
      title: 'Daily Planner',
      description: 'Full-stack planner with user authentication and cloud sync',
      path: '/daily-planner',
      icon: '📅',
      tech: ['React', 'MongoDB', 'Auth', 'API']
    },
    {
      title: 'Task List',
      description: 'Productivity app for managing tasks',
      path: '/task-list',
      icon: '📝',
      tech: ['React', 'Local Storage', 'CRUD']
    }
  ];

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <p className="hero-intro">Hi, I'm Parker</p>
            <h1 className="hero-title">
              <span className="title-line">Frontend Developer</span>
              <span className="title-line">Building Interactive Web Experiences</span>
            </h1>
            <p className="hero-subtitle">
              Junior developer with a finance background, specializing in React, TypeScript, and creating practical web applications
            </p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">6</span>
                <span className="stat-label">Projects</span>
              </div>
              <div className="stat">
                <span className="stat-number">React</span>
                <span className="stat-label">Stack</span>
              </div>
              <div className="stat">
                <span className="stat-number">∞</span>
                <span className="stat-label">Learning</span>
              </div>
            </div>
          </div>
          <div className="hero-graphic">
            <SpinningGraphic />
          </div>
        </div>
      </section>

      <section className="projects">
        <div className="projects-container">
          <h2 className="projects-title">Featured Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <Link 
                key={index} 
                to={project.path} 
                className="project-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="project-icon">{project.icon}</div>
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                <div className="project-tech">
                  {project.tech.map((tech, techIndex) => (
                    <span key={techIndex} className="tech-tag">{tech}</span>
                  ))}
                </div>
                <div className="project-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="technologies">
        <div className="technologies-container">
          <h2 className="technologies-title">Technologies Used</h2>

          <div className="tech-category">
            <h3 className="tech-category-title">Primary Languages & Technologies</h3>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">Frontend (Client-Side)</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>TypeScript</strong> (.tsx, .ts files) - Main language for React components and logic</span>
                <span className="tech-item"><strong>React</strong> (v18.2.0) - JavaScript library for building the user interface</span>
                <span className="tech-item"><strong>CSS3</strong> - Styling for all components and animations</span>
                <span className="tech-item"><strong>HTML5</strong> - Structure (generated by React)</span>
                <span className="tech-item"><strong>JSX/TSX</strong> - React component syntax</span>
              </div>
            </div>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">Backend (Server-Side)</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>Node.js</strong> - JavaScript runtime for the server</span>
                <span className="tech-item"><strong>JavaScript</strong> (ES6+) - Server-side logic in contact-form-server.js</span>
              </div>
            </div>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">Automation & Deployment</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>Batch Scripts</strong> (.bat files) - Windows automation</span>
                <span className="tech-item"><strong>PowerShell</strong> (.ps1 files) - Windows scripting</span>
                <span className="tech-item"><strong>Shell Scripts</strong> (.sh files) - Unix/Linux automation</span>
              </div>
            </div>
          </div>

          <div className="tech-category">
            <h3 className="tech-category-title">Key Libraries & Frameworks</h3>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">React Ecosystem</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>React Router DOM</strong> (v6.3.0) - Client-side routing</span>
                <span className="tech-item"><strong>React Scripts</strong> (v5.0.1) - Build tooling</span>
                <span className="tech-item"><strong>React Testing Library</strong> - Testing utilities</span>
              </div>
            </div>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">Backend Dependencies</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>Express.js</strong> (v4.18.2) - Web server framework</span>
                <span className="tech-item"><strong>Nodemailer</strong> (v6.9.1) - Email sending</span>
                <span className="tech-item"><strong>CORS</strong> (v2.8.5) - Cross-origin resource sharing</span>
                <span className="tech-item"><strong>Express Rate Limit</strong> (v6.7.0) - API rate limiting</span>
                <span className="tech-item"><strong>dotenv</strong> (v16.0.3) - Environment variable management</span>
              </div>
            </div>

            <div className="tech-subcategory">
              <h4 className="tech-subcategory-title">Development Tools</h4>
              <div className="tech-list">
                <span className="tech-item"><strong>TypeScript</strong> (v4.7.4) - Type checking and compilation</span>
                <span className="tech-item"><strong>ESLint</strong> - Code linting and formatting</span>
                <span className="tech-item"><strong>Jest</strong> - Testing framework</span>
                <span className="tech-item"><strong>gh-pages</strong> - GitHub Pages deployment</span>
                <span className="tech-item"><strong>Concurrently</strong> - Running multiple scripts</span>
              </div>
            </div>
          </div>

          <div className="tech-summary">
            <h3 className="tech-summary-title">Project Structure Summary</h3>
            <div className="tech-summary-grid">
              <div className="tech-summary-item">
                <strong>Frontend Components:</strong> 6 main pages (Home, Classic Games Collection, Task List, Web Design Services, etc.) all written in TypeScript React
              </div>
              <div className="tech-summary-item">
                <strong>Styling:</strong> Individual CSS files for each component with custom animations
              </div>
              <div className="tech-summary-item">
                <strong>Games:</strong> Multiple browser games built in TypeScript (Snake, Tetris, 2048, Pong)
              </div>
              <div className="tech-summary-item">
                <strong>Backend:</strong> Node.js/Express server with Nodemailer for email functionality
              </div>
              <div className="tech-summary-item">
                <strong>Deployment:</strong> Automated with batch/PowerShell scripts for GitHub Pages
              </div>
            </div>
            <p className="tech-conclusion">
              This is a <strong>full-stack TypeScript React application</strong> with a Node.js backend, featuring custom CSS animations, multiple interactive games, and comprehensive deployment automation scripts for both Windows and Unix environments.
            </p>
          </div>
        </div>
      </section>

      <section id="about" className="about">
        <div className="about-container">
          <h2 className="about-title">About Me</h2>
          <div className="about-content">
            <p className="about-text">
              I'm a junior frontend developer with a unique background in finance and accounting.
              I specialize in React, TypeScript, and building practical web applications that solve
              real-world problems.
            </p>
            <p className="about-text">
              Before transitioning to web development, I spent years designing complex Excel spreadsheets
              with automated functions, programming VBA macros, and building Google Apps Scripts to streamline
              data workflows. I've automated the processing of exported CSV files into clean, structured Excel
              data with properly aligned headers and validation—skills that translate directly into building
              efficient, data-driven web applications.
            </p>
            <p className="about-text">
              I'm passionate about creating clean, user-friendly interfaces and writing maintainable code.
              Currently seeking junior developer positions where I can contribute my unique blend of technical
              and business skills while continuing to learn and grow.
            </p>
            <div className="about-skills">
              <div className="skill-category">
                <h3>Frontend</h3>
                <p>React, TypeScript, CSS3, HTML5</p>
              </div>
              <div className="skill-category">
                <h3>Backend</h3>
                <p>Node.js, Express, MongoDB</p>
              </div>
              <div className="skill-category">
                <h3>Automation</h3>
                <p>Excel VBA, Google Apps Script</p>
              </div>
              <div className="skill-category">
                <h3>Tools</h3>
                <p>Git, VS Code, npm, Vercel</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <div className="contact-container">
          <h2 className="contact-title">Connect With Me</h2>
          <p className="contact-description">
            Let's connect! Reach out via email.
          </p>
          <div className="contact-methods">
            <a href="mailto:parkeradams247@gmail.com" className="contact-method">
              <span className="contact-icon">📧</span>
              <span className="contact-label">Email</span>
              <span className="contact-value">parkeradams247@gmail.com</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;